import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../../api/client";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";
import { BookmarkSimple, Plus, X, Star } from "@phosphor-icons/react";

/**
 * Compact preset row for the Dashboard. Fetches, applies, saves and deletes
 * saved filter presets scoped to the current user + project.
 *
 * Props:
 * - projectId      : current project id (required)
 * - filters        : current filter state (used when saving)
 * - sort           : current sort state (used when saving)
 * - onApply({filters, sort}) : called with a preset's state to apply it
 */
export default function FilterPresets({ projectId, filters, sort, onApply }) {
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [activeId, setActiveId] = useState(null);

  const reload = useCallback(async () => {
    if (!projectId) { setPresets([]); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/filter-presets", { params: { project_id: projectId } });
      setPresets(data.presets || []);
    } catch {
      setPresets([]);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { reload(); setActiveId(null); }, [reload]);

  const hasActiveFilters =
    (filters && Object.values(filters).some((v) => v !== null && v !== undefined && String(v).length > 0)) ||
    (sort && sort.by);

  const handleApply = (preset) => {
    setActiveId(preset.id);
    onApply({ filters: preset.filters || {}, sort: preset.sort || { by: null, dir: "asc" } });
    toast.success(`Applied preset "${preset.name}"`);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    setSaving(true);
    try {
      const { data } = await api.post("/filter-presets", {
        project_id: projectId,
        name: trimmed,
        filters: filters || {},
        sort: sort || {},
      });
      toast.success(`Preset "${data.name}" saved`);
      setName("");
      setSaveOpen(false);
      await reload();
      setActiveId(data.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save preset");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (preset, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete preset "${preset.name}"?`)) return;
    try {
      await api.delete(`/filter-presets/${preset.id}`);
      toast.success(`Deleted "${preset.name}"`);
      if (activeId === preset.id) setActiveId(null);
      await reload();
    } catch {
      toast.error("Could not delete preset");
    }
  };

  return (
    <div
      className="flex items-center gap-2 flex-wrap"
      data-testid="filter-presets"
    >
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-neutral-500 font-mono shrink-0">
        <BookmarkSimple size={12} weight="bold" />
        Presets
      </div>

      {loading && presets.length === 0 ? (
        <span className="text-xs text-neutral-400">Loading…</span>
      ) : null}

      {!loading && presets.length === 0 ? (
        <span className="text-xs text-neutral-400" data-testid="presets-empty">
          None saved yet
        </span>
      ) : null}

      {presets.map((p) => {
        const isActive = p.id === activeId;
        return (
          <button
            key={p.id}
            type="button"
            onClick={() => handleApply(p)}
            data-testid={`preset-chip-${p.id}`}
            className={`group inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full border text-xs transition-colors ${
              isActive
                ? "bg-[#387EE6] border-[#2563EB] text-white"
                : "bg-white border-neutral-200 text-neutral-700 hover:border-[#387EE6] hover:text-[#387EE6]"
            }`}
            title={`${Object.keys(p.filters || {}).length} filter(s)${p.sort?.by ? ` · sort ${p.sort.by} ${p.sort.dir}` : ""}`}
          >
            {isActive ? (
              <Star size={11} weight="fill" />
            ) : (
              <BookmarkSimple size={11} weight="bold" />
            )}
            <span className="max-w-[140px] truncate">{p.name}</span>
            <span
              role="button"
              aria-label={`Delete preset ${p.name}`}
              tabIndex={0}
              onClick={(e) => handleDelete(p, e)}
              onKeyDown={(e) => { if (e.key === "Enter") handleDelete(p, e); }}
              className={`ml-0.5 h-4 w-4 flex items-center justify-center rounded-full opacity-0 group-hover:opacity-100 transition-opacity ${
                isActive ? "hover:bg-white/20" : "hover:bg-red-50 hover:text-red-600"
              }`}
              data-testid={`preset-delete-${p.id}`}
            >
              <X size={10} weight="bold" />
            </span>
          </button>
        );
      })}

      <button
        type="button"
        onClick={() => { setName(""); setSaveOpen(true); }}
        disabled={!hasActiveFilters || !projectId}
        data-testid="save-preset-btn"
        className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full border border-dashed border-neutral-300 text-xs text-neutral-600 hover:border-[#387EE6] hover:text-[#387EE6] disabled:opacity-40 disabled:hover:border-neutral-300 disabled:hover:text-neutral-600 transition-colors"
        title={hasActiveFilters ? "Save current filters as a preset" : "Apply at least one filter to save a preset"}
      >
        <Plus size={11} weight="bold" />
        Save current
      </button>

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="max-w-md" data-testid="save-preset-dialog">
          <DialogHeader>
            <DialogTitle>Save filter preset</DialogTitle>
            <DialogDescription>
              Store the current filters &amp; sort under a short name so you can re-apply them in one click.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-3">
            <Input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. My blockers"
              maxLength={60}
              data-testid="save-preset-name-input"
              className="h-10"
            />
            <div className="text-[11px] text-neutral-500 font-mono">
              {Object.entries(filters || {}).filter(([, v]) => v).length} filter(s)
              {sort?.by ? ` · sort ${sort.by} ${sort.dir}` : ""}
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setSaveOpen(false)}
                data-testid="save-preset-cancel"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={saving || !name.trim()}
                data-testid="save-preset-confirm"
                className="bg-[#387EE6] hover:bg-[#2563EB] text-white"
              >
                {saving ? "Saving..." : "Save preset"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
